/*
 * <product-tabs> — responsive tabs/accordion for the Product Details section.
 *
 * Desktop (>= data-breakpoint px): horizontal tabs. Clicking a tab (or arrow
 * keys on the tablist) shows exactly one panel.
 * Mobile (< breakpoint): the tab nav is hidden and each panel becomes a
 * single-open accordion driven by its own header; clicking the open header
 * collapses it.
 *
 * Both affordances control the same panels via the `hidden` attribute, so no
 * markup is duplicated beyond the (visually hidden per breakpoint) headers.
 *
 * Read more/less: when data-collapsible is on, each panel's content is wrapped
 * in a clamped ".product-tabs__reveal" with a fade overlay and a "Read more"
 * toggle. A panel is only clamped if its content is meaningfully taller than
 * the collapsed height. Panels are `hidden` until shown, so measurement happens
 * lazily the first time a panel becomes visible (inside render()).
 */
class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.breakpoint = parseInt(this.dataset.breakpoint, 10) || 750;
    this.tabs = Array.from(this.querySelectorAll('.product-tabs__tab'));
    this.headers = Array.from(this.querySelectorAll('.product-tabs__accordion-header'));
    this.panels = Array.from(this.querySelectorAll('.product-tabs__panel'));
    // First rendered tab (index 0) is active by default.
    this.activeIndex = this.panels.length ? 0 : -1;

    // Read more/less config.
    this.collapsible = this.dataset.collapsible === 'true';
    this.collapsedHeight = parseInt(this.dataset.collapsedHeight, 10) || 260;
    this.moreLabel = this.dataset.moreLabel || 'Read more';
    this.lessLabel = this.dataset.lessLabel || 'Read less';
    // Only clamp when content exceeds the preview by at least this much.
    this.slack = 40;
  }

  connectedCallback() {
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.activate(i));
      tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
    });

    this.headers.forEach((header, i) => {
      header.addEventListener('click', () => {
        // Accordion: clicking the open item collapses it.
        this.activate(this.activeIndex === i ? -1 : i);
      });
    });

    if (this.collapsible) {
      this.panels.forEach((panel) => this.buildReveal(panel));
      this.classList.add('pdt-ready');
    }

    this.mql = window.matchMedia(`(min-width: ${this.breakpoint}px)`);
    this.onBreakpointChange = () => {
      // Desktop always keeps one panel open.
      if (this.mql.matches && this.activeIndex < 0) this.activate(0);
    };
    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onBreakpointChange);
    } else {
      this.mql.addListener(this.onBreakpointChange);
    }

    this.render();
  }

  disconnectedCallback() {
    if (!this.mql) return;
    if (this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this.onBreakpointChange);
    } else {
      this.mql.removeListener(this.onBreakpointChange);
    }
  }

  activate(index) {
    this.activeIndex = index;
    this.render();
  }

  render() {
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== this.activeIndex;
    });

    this.tabs.forEach((tab, i) => {
      const selected = i === this.activeIndex;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: only the active tab is in the tab order.
      tab.tabIndex = selected ? 0 : -1;
    });

    this.headers.forEach((header, i) => {
      const open = i === this.activeIndex;
      header.classList.toggle('is-active', open);
      header.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Measure the panel that just became visible (hidden panels have no height).
    if (this.collapsible && this.activeIndex >= 0) {
      const panel = this.panels[this.activeIndex];
      if (panel && panel.dataset.pdtMeasured !== 'true') {
        // Defer one frame so layout is settled after unhiding.
        requestAnimationFrame(() => this.measurePanel(panel));
      }
    }
  }

  /* Wrap a panel's content in a reveal + fade + toggle button (once). */
  buildReveal(panel) {
    if (panel.dataset.pdtBuilt === 'true') return;

    const reveal = document.createElement('div');
    reveal.className = 'product-tabs__reveal';
    while (panel.firstChild) reveal.appendChild(panel.firstChild);

    const fade = document.createElement('span');
    fade.className = 'product-tabs__fade';
    fade.setAttribute('aria-hidden', 'true');
    reveal.appendChild(fade);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-tabs__toggle';
    button.setAttribute('aria-expanded', 'false');
    if (panel.id) button.setAttribute('aria-controls', panel.id);
    button.innerHTML =
      `<span class="product-tabs__toggle-label"></span>` +
      `<span class="product-tabs__toggle-caret" aria-hidden="true"></span>`;
    button.querySelector('.product-tabs__toggle-label').textContent = this.moreLabel;
    button.addEventListener('click', () => this.toggle(panel));

    panel.appendChild(reveal);
    panel.appendChild(button);
    panel.dataset.pdtBuilt = 'true';
    panel._pdtReveal = reveal;
    panel._pdtButton = button;
  }

  /* Decide whether a now-visible panel needs the Read more affordance. */
  measurePanel(panel) {
    const reveal = panel._pdtReveal;
    if (!reveal || panel.hidden) return;
    panel.dataset.pdtMeasured = 'true';

    if (reveal.scrollHeight > this.collapsedHeight + this.slack) {
      // Apply the collapsed state WITHOUT a transition (no shrink animation on
      // first view); enable transitions only afterwards, for user toggles.
      panel.classList.add('is-collapsible', 'is-collapsed');
      requestAnimationFrame(() => panel.classList.add('pdt-anim'));
    } else {
      // Short enough — show it all, no toggle/fade.
      panel.classList.remove('is-collapsible', 'is-collapsed', 'is-expanded');
    }
  }

  /* Expand/collapse a panel with an interrupt-safe max-height transition. */
  toggle(panel) {
    const reveal = panel._pdtReveal;
    const button = panel._pdtButton;
    if (!reveal || !button) return;

    const label = button.querySelector('.product-tabs__toggle-label');
    const willExpand = panel.classList.contains('is-collapsed');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Cancel any transition handler still pending from a previous toggle, so a
    // stale "expand finished" callback can't reopen a panel we're collapsing.
    if (panel._pdtOnEnd) {
      reveal.removeEventListener('transitionend', panel._pdtOnEnd);
      panel._pdtOnEnd = null;
    }

    // Flip state + button up front.
    panel.classList.toggle('is-expanded', willExpand);
    panel.classList.toggle('is-collapsed', !willExpand);
    button.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
    if (label) label.textContent = willExpand ? this.lessLabel : this.moreLabel;

    if (reduce) {
      reveal.style.maxHeight = willExpand ? 'none' : '';
      return;
    }

    // Animate from the panel's current rendered height (works mid-animation too)
    // to an explicit target height — never to/from `none`, which can't tween.
    const startPx = reveal.getBoundingClientRect().height;
    const targetPx = willExpand ? reveal.scrollHeight : this.collapsedHeight;

    reveal.style.maxHeight = `${startPx}px`;
    void reveal.offsetHeight; // commit the start value
    reveal.style.maxHeight = `${targetPx}px`;

    const onEnd = (e) => {
      if (e.target !== reveal || e.propertyName !== 'max-height') return;
      reveal.removeEventListener('transitionend', onEnd);
      panel._pdtOnEnd = null;
      // Only normalise if the panel is still in the state this run targeted.
      if (willExpand) {
        if (panel.classList.contains('is-expanded')) reveal.style.maxHeight = 'none';
      } else if (panel.classList.contains('is-collapsed')) {
        reveal.style.maxHeight = ''; // hand height back to the CSS clamp
      }
    };
    panel._pdtOnEnd = onEnd;
    reveal.addEventListener('transitionend', onEnd);
  }

  onKeydown(event, index) {
    const { key } = event;
    let next = null;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      next = (index + 1) % this.tabs.length;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      next = (index - 1 + this.tabs.length) % this.tabs.length;
    } else if (key === 'Home') {
      next = 0;
    } else if (key === 'End') {
      next = this.tabs.length - 1;
    }

    if (next === null) return;
    event.preventDefault();
    this.activate(next);
    this.tabs[next].focus();
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
